import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { ShoppingCart, Sparkles, ArrowLeft, Check, Download, Star, MessageSquare, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function DesignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [design, setDesign] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchDesign();
    fetchReviews();
  }, [id]);

  useEffect(() => {
    if (user && reviews.length > 0) {
      const userReview = reviews.find((r) => r.user_id === user.id);
      setHasReviewed(!!userReview);
    }
  }, [user, reviews]);

  const fetchDesign = async () => {
    try {
      const response = await axios.get(`${API}/designs/${id}`);
      setDesign(response.data);
      
      // Check if user can review (has purchased)
      if (user) {
        try {
          const ordersRes = await axios.get(`${API}/orders`);
          const hasPurchased = ordersRes.data.some(
            (order) => order.items?.includes(id) && order.status === "completed"
          );
          setCanReview(hasPurchased);
        } catch (e) {
          console.error("Failed to check purchase status");
        }
      }
    } catch (e) {
      toast.error("Design not found");
      navigate("/marketplace");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/design/${id}`);
      setReviews(response.data);
    } catch (e) {
      console.error("Failed to fetch reviews");
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please sign in to add to cart");
      navigate("/auth");
      return;
    }

    setAddingToCart(true);
    try {
      await axios.post(`${API}/cart`, { design_id: design.id });
      toast.success("Added to cart!");
    } catch (e) {
      if (e.response?.data?.detail === "Already in cart") {
        toast.info("Already in cart");
      } else {
        toast.error("Failed to add to cart");
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please sign in to leave a review");
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        design_id: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment || null,
      });
      toast.success("Review submitted!");
      setShowReviewForm(false);
      setReviewForm({ rating: 5, comment: "" });
      fetchReviews();
      fetchDesign(); // Refresh design to get updated rating
      setHasReviewed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => onSelect(star) : undefined}
            className={`${interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
            disabled={!interactive}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-zinc-200 text-zinc-200"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="skeleton-pulse aspect-square rounded-2xl"></div>
              <div className="space-y-4">
                <div className="skeleton-pulse h-8 rounded w-3/4"></div>
                <div className="skeleton-pulse h-4 rounded w-1/2"></div>
                <div className="skeleton-pulse h-32 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!design) return null;

  const avgRating = design.avg_rating || 0;
  const reviewCount = design.review_count || reviews.length;

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="design-detail-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-zinc-100 transparency-grid">
                <img
                  src={design.image_url}
                  alt={design.title}
                  className="w-full h-full object-contain"
                  data-testid="design-image"
                />
              </div>
              
              {design.is_ai_generated && (
                <div className="absolute top-4 left-4 badge-ai flex items-center gap-1.5 px-3 py-1.5">
                  <Sparkles className="w-4 h-4" />
                  AI Generated
                </div>
              )}
            </div>

            {/* Details */}
            <div className="lg:py-8">
              <p className="overline mb-2">{design.category.toUpperCase()}</p>
              <h1 
                className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="design-title"
              >
                {design.title}
              </h1>

              {/* Rating Display */}
              <div className="flex items-center gap-2 mb-4">
                {renderStars(Math.round(avgRating))}
                <span className="text-sm text-zinc-500">
                  {avgRating > 0 ? avgRating.toFixed(1) : "No ratings"} 
                  {reviewCount > 0 && ` (${reviewCount} review${reviewCount !== 1 ? "s" : ""})`}
                </span>
              </div>

              <p className="text-3xl font-semibold price-tag mb-6" data-testid="design-price">
                ${design.price.toFixed(2)}
              </p>

              <p className="text-zinc-600 mb-8 leading-relaxed" data-testid="design-description">
                {design.description}
              </p>

              {/* Tags */}
              {design.tags && design.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {design.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  className="btn-primary flex items-center justify-center gap-2 flex-1"
                  data-testid="add-to-cart-btn"
                >
                  {addingToCart ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner"></div>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>

              {/* Features */}
              <div className="border-t border-zinc-200 pt-8">
                <h3 className="font-medium mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-zinc-600">
                    <Check className="w-5 h-5 text-green-500" />
                    High-resolution PNG file (3000x3000px)
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-600">
                    <Check className="w-5 h-5 text-green-500" />
                    Transparent background
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-600">
                    <Check className="w-5 h-5 text-green-500" />
                    Commercial license included
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-600">
                    <Check className="w-5 h-5 text-green-500" />
                    Ready for print-on-demand
                  </li>
                  <li className="flex items-center gap-3 text-sm text-zinc-600">
                    <Download className="w-5 h-5 text-indigo-500" />
                    Instant download after purchase
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="mt-16 border-t border-zinc-200 pt-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 
                  className="text-2xl font-semibold"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Customer Reviews
                </h2>
                <p className="text-zinc-500 mt-1">
                  {reviews.length} review{reviews.length !== 1 ? "s" : ""}
                </p>
              </div>
              
              {canReview && !hasReviewed && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="write-review-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Write a Review
                </button>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <div className="bg-white p-6 rounded-xl border border-zinc-200 mb-8 animate-fade-in">
                <h3 className="font-medium mb-4">Write Your Review</h3>
                <form onSubmit={handleSubmitReview}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Rating</label>
                    {renderStars(reviewForm.rating, true, (star) =>
                      setReviewForm({ ...reviewForm, rating: star })
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">
                      Comment (optional)
                    </label>
                    <Textarea
                      value={reviewForm.comment}
                      onChange={(e) =>
                        setReviewForm({ ...reviewForm, comment: e.target.value })
                      }
                      placeholder="Share your experience with this design..."
                      className="min-h-[100px]"
                      data-testid="review-comment-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submittingReview}
                      className="btn-primary flex items-center gap-2"
                      data-testid="submit-review-btn"
                    >
                      {submittingReview ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Submit Review
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <MessageSquare className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">No reviews yet</p>
                {canReview && !hasReviewed && (
                  <p className="text-sm text-zinc-400 mt-1">
                    Be the first to review this design!
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-white p-6 rounded-xl border border-zinc-200"
                    data-testid={`review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 text-sm font-medium">
                              {review.user_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium">{review.user_name}</span>
                        </div>
                        {renderStars(review.rating)}
                      </div>
                      <span className="text-xs text-zinc-400">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-zinc-600 mt-3">{review.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
